/**
 * Cart Shipping Threshold Calculator
 * Ensures the free shipping threshold is properly converted for different currencies
 * Works with the existing cart drawer components and updates live with smooth animations
 */
// Cart totals: commas become dots and only the last dot stays, so R1,000.00 parses as 1000 (not 1).
class ShippingThresholdCalculator {
  constructor() {
    // Base threshold in GBP
    this.baseThresholdGBP = 50;
    
    // Currency conversion rates (update these with current rates)
    this.currencyRates = {
      'GBP': 1,       // Base currency
      'USD': 1.30,    // 1 GBP = 1.30 USD
      'AUD': 2.14,    // 1 GBP = 2.14 AUD
      'EUR': 1.16,    // 1 GBP = 1.16 EUR
      'CAD': 1.78,    // 1 GBP = 1.78 CAD
      'ZAR': 20.0     // 1 GBP = 20.0 ZAR
    };
    
    // Store previous values for smooth animations
    this.previousCartTotal = 0;
    this.previousPercent = 0;
    
    // Initialize
    this.init();
  }
  
  init() {
    // Get current currency
    this.currentCurrency = Shopify && Shopify.currency ? Shopify.currency.active : 'GBP';
    
    // Calculate threshold in current currency
    this.calculateThreshold();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial update with a slight delay to ensure DOM is ready
    setTimeout(() => {
      this.updateUI();
    }, 50);
  }
  
  calculateThreshold() {
    // Default to GBP threshold if currency not found
    this.currencyThreshold = this.baseThresholdGBP;
    
    // Convert to current currency if rate exists
    if (this.currencyRates[this.currentCurrency]) {
      this.currencyThreshold = Math.round(this.baseThresholdGBP * this.currencyRates[this.currentCurrency]);
    }
    
    // Fixed amounts match the Shopify delivery-profile free-shipping thresholds.
    const fixed = { GBP: 50, EUR: 55, USD: 65, AUD: 90, CAD: 90, ZAR: 1000 }[this.currentCurrency];
    if (fixed) this.currencyThreshold = fixed;
    
    return this.currencyThreshold;
  }
  
  setupEventListeners() {
    // Listen for cart updates from the pubsub system
    if (typeof subscribe !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe('cart-update', (event) => {
        // Update after a short delay to ensure DOM is updated
        setTimeout(() => {
          this.updateUI();
        }, 100);
      });
    }
    
    // Update when cart drawer opens
    this.setupCartDrawerListener();
    
    // Also update on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.updateUI.bind(this));
    } else {
      this.updateUI();
    }
  }
  
  setupCartDrawerListener() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const originalOpen = cartDrawer.open;
      const self = this;
      
      cartDrawer.open = function(...args) {
        originalOpen.apply(this, args);
        // Dispatch custom event for other listeners
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        setTimeout(() => {
          self.updateUI();
        }, 100);
      };
    }
  }
  
  updateUI() {
    // Get cart total from the checkout button or totals element
    let cartTotal = 0;
    const checkoutButton = document.querySelector('#CartDrawer-Checkout .totals__total-value');
    const totalsElement = document.querySelector('.cart-drawer__footer .totals__total-value');
    
    if (checkoutButton) {
      const dataAttr = checkoutButton.getAttribute('data');
      if (dataAttr) {
        cartTotal = parseFloat(dataAttr.replace(/,/g, '.').replace(/\.(?=.*\.)/g, ''));
      }
    } else if (totalsElement) {
      const dataAttr = totalsElement.getAttribute('data');
      if (dataAttr) {
        cartTotal = parseFloat(dataAttr.replace(/,/g, '.').replace(/\.(?=.*\.)/g, ''));
      }
    }
    
    // Fallback: try to get from any totals element with data attribute
    if (isNaN(cartTotal)) {
      const allTotalsElements = document.querySelectorAll('.totals__total-value[data]');
      for (const element of allTotalsElements) {
        const dataAttr = element.getAttribute('data');
        if (dataAttr) {
          cartTotal = parseFloat(dataAttr.replace(/,/g, '.').replace(/\.(?=.*\.)/g, ''));
          if (!isNaN(cartTotal)) break;
        }
      }
    }
    
    if (isNaN(cartTotal)) {
      cartTotal = 0;
    }
    
    // Fallback: try to get cart total from text content
    if (cartTotal === 0) {
      const totalTextElements = document.querySelectorAll('.totals__total-value');
      for (const element of totalTextElements) {
        const text = element.textContent.trim();
        // Try to extract number from text like "$0.00" or "£0.00"
        const match = text.match(/[\d,]+\.?\d*/);
        if (match) {
          const extractedTotal = parseFloat(match[0].replace(/,/g, '.').replace(/\.(?=.*\.)/g, ''));
          if (!isNaN(extractedTotal)) {
            cartTotal = extractedTotal;
            break;
          }
        }
      }
    }
    
    // Check if cart is empty (no items) vs cart has items but total is 0
    const cartItems = document.querySelectorAll('.cart-item');
    const hasItems = cartItems.length > 0;
    
    // Alternative method: check if cart drawer is not empty
    const cartDrawer = document.querySelector('cart-drawer');
    const isCartEmpty = cartDrawer && cartDrawer.classList.contains('is-empty');
    const hasItemsAlt = !isCartEmpty;
    
    // Use the more reliable method
    const finalHasItems = hasItems || hasItemsAlt;
    
    // Always update on first run (previousCartTotal is 0)
    if (cartTotal === this.previousCartTotal && this.previousCartTotal !== 0) {
      return;
    }
    
    this.previousCartTotal = cartTotal;
    
    // Calculate remaining amount and progress percentage
    const remaining = Math.max(0, this.currencyThreshold - cartTotal);
    let percent = (cartTotal / this.currencyThreshold) * 100;
    percent = Math.min(100, Math.max(0, percent)); // Clamp between 0-100
    
    // If cart has items but total is 0 (free items), show 100% progress
    if (finalHasItems && cartTotal === 0) {
      percent = 100;
    }
    
    // Update progress bar with smooth animation
    this.updateProgressBar(percent);
    
    // Update progress container class
    this.updateProgressClasses(percent);
    
    // Update progress_shipping complete class
    this.updateCompleteState(percent);
    
    // Update text elements
    this.updateTextElements(remaining, percent);
    
    // Update currency symbol
    this.updateCurrencySymbol();
    
    this.previousPercent = percent;
  }
  
  updateProgressBar(percent) {
    const progressBar = document.querySelector('.progress_shipping .progress-meter');
    if (progressBar) {
      // Get current width to prevent flashing
      const currentWidth = parseFloat(progressBar.style.width) || 0;
      
      // Only animate if there's a significant change or if it's the first update
      if (Math.abs(currentWidth - percent) > 1 || this.previousCartTotal === 0) {
        // Add smooth transition
        progressBar.style.transition = 'width 0.6s ease-out';
        progressBar.style.width = `${percent}%`;
        
        // Remove transition after animation completes
        setTimeout(() => {
          progressBar.style.transition = '';
        }, 600);
      } else {
        // For small changes, update without animation to prevent flashing
        progressBar.style.transition = 'none';
        progressBar.style.width = `${percent}%`;
        // Re-enable transitions after a brief moment
        setTimeout(() => {
          progressBar.style.transition = 'width 0.6s ease-out';
        }, 50);
      }
    }
  }
  
  updateProgressClasses(percent) {
    const progressContainer = document.querySelector('.progress');
    if (progressContainer) {
      // Remove all progress classes
      progressContainer.classList.remove('progress-0', 'progress-25', 'progress-50', 'progress-75', 'progress-100');
      
      // Add appropriate class
      if (percent >= 100) {
        progressContainer.classList.add('progress-100');
      } else if (percent >= 75) {
        progressContainer.classList.add('progress-75');
      } else if (percent >= 50) {
        progressContainer.classList.add('progress-50');
      } else if (percent >= 25) {
        progressContainer.classList.add('progress-25');
      } else {
        progressContainer.classList.add('progress-0');
      }
    }
  }
  
  updateCompleteState(percent) {
    const progressShipping = document.querySelector('.progress_shipping');
    if (progressShipping) {
      if (percent >= 100) {
        progressShipping.classList.add('complete');
      } else {
        progressShipping.classList.remove('complete');
      }
    }
  }
  
  updateTextElements(remaining, percent) {
    // Update progress amount text
    const progressAmount = document.querySelector('.progress_amount');
    if (progressAmount) {
      // Handle 0 cart total case
      if (this.previousCartTotal === 0) {
        progressAmount.textContent = this.currencyThreshold.toFixed(2);
      } else {
        progressAmount.textContent = remaining.toFixed(2);
      }
    }
    
    // Check if cart has items but total is 0 (free items)
    const cartItems = document.querySelectorAll('.cart-item');
    const hasItems = cartItems.length > 0;
    const cartDrawer = document.querySelector('cart-drawer');
    const isCartEmpty = cartDrawer && cartDrawer.classList.contains('is-empty');
    const finalHasItems = hasItems || !isCartEmpty;
    
    // Show free shipping message or remaining amount message
    const progressRemain = document.querySelector('.progress_remain');
    const freeShipping = document.querySelector('.free_shipping');
    
    if (progressRemain && freeShipping) {
      // If cart has items but total is 0, show free shipping message
      if (finalHasItems && this.previousCartTotal === 0) {
        // Hide progress message first, then show free shipping
        progressRemain.style.opacity = '0';
        setTimeout(() => {
          progressRemain.style.display = 'none';
          freeShipping.style.display = 'block';
          // Force reflow
          freeShipping.offsetHeight;
          freeShipping.style.opacity = '1';
        }, 150);
        return;
      }
      
      // Ensure only one message is visible at a time
      if (percent >= 100) {
        // Hide progress message first, then show free shipping
        progressRemain.style.opacity = '0';
        setTimeout(() => {
          progressRemain.style.display = 'none';
          freeShipping.style.display = 'block';
          // Force reflow
          freeShipping.offsetHeight;
          freeShipping.style.opacity = '1';
        }, 150);
      } else {
        // Hide free shipping message first, then show progress
        freeShipping.style.opacity = '0';
        setTimeout(() => {
          freeShipping.style.display = 'none';
          progressRemain.style.display = 'block';
          // Force reflow
          progressRemain.offsetHeight;
          progressRemain.style.opacity = '1';
        }, 150);
      }
    }
    
    // Update shipping text
    const shippingText = document.querySelector('.shipping');
    if (shippingText) {
      if (percent >= 100) {
        shippingText.textContent = 'FREE Shipping';
        shippingText.style.color = '#00a651'; // Green color for free shipping
      } else {
        shippingText.textContent = 'FREE Shipping';
        shippingText.style.color = ''; // Reset to default color
      }
    }
  }
  
  updateCurrencySymbol() {
    const currencySymbol = document.querySelector('.currency_symbol');
    if (currencySymbol) {
      currencySymbol.textContent = this.getCurrencySymbol();
    }
  }
  
  getCurrencySymbol() {
    const symbols = {
      'GBP': '£',
      'USD': '$',
      'AUD': 'A$',
      'EUR': '€',
      'CAD': 'C$',
      'ZAR': 'R'
    };
    
    return symbols[this.currentCurrency] || '£';
  }
  
  disconnect() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize the shipping threshold calculator after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.shippingThresholdCalculator = new ShippingThresholdCalculator();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  window.shippingThresholdCalculator = new ShippingThresholdCalculator();
}

// Initialize when cart drawer opens (in case it's loaded dynamically)
document.addEventListener('cart-drawer:open', () => {
  if (window.shippingThresholdCalculator) {
    setTimeout(() => {
      window.shippingThresholdCalculator.updateUI();
    }, 100);
  }
});

// Also initialize when cart drawer is created (for dynamic loading)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'CART-DRAWER') {
        if (window.shippingThresholdCalculator) {
          window.shippingThresholdCalculator.setupCartDrawerListener();
          setTimeout(() => {
            window.shippingThresholdCalculator.updateUI();
          }, 100);
        }
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Hook into the Cart Drawer class to update shipping threshold on cart changes
class CartDrawerExtension {
  static initialize() {
    // Wait for CartDrawer to be defined
    if (typeof CartDrawer !== 'undefined') {
      this.setupCartDrawerHook();
    } else {
      // Check periodically for CartDrawer
      const checkInterval = setInterval(() => {
        if (typeof CartDrawer !== 'undefined') {
          this.setupCartDrawerHook();
          clearInterval(checkInterval);
        }
      }, 100);
    }
  }
  
  static setupCartDrawerHook() {
    const originalRenderContents = CartDrawer.prototype.renderContents;
    
    CartDrawer.prototype.renderContents = function(...args) {
      originalRenderContents.apply(this, args);
      
      // Update shipping threshold after cart contents are rendered
      if (window.shippingThresholdCalculator) {
        setTimeout(() => {
          window.shippingThresholdCalculator.updateUI();
        }, 100);
      }
    };
  }
}

// Initialize extension
CartDrawerExtension.initialize();

// Also hook into the cart-items updateQuantity method
if (typeof CartItems !== 'undefined') {
  const originalUpdateQuantity = CartItems.prototype.updateQuantity;
  CartItems.prototype.updateQuantity = function(...args) {
    const result = originalUpdateQuantity.apply(this, args);
    
    // Update shipping threshold after quantity update
    if (window.shippingThresholdCalculator) {
      setTimeout(() => {
        window.shippingThresholdCalculator.updateUI();
      }, 200);
    }
    
    return result;
  };
}
